import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Divider,
  FormControl,
  NativeSelect,
  Typography,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LocaleKeys from '../locales/keys';
import useSearchOrders from '../common/hooks/useSearchOrder';
import useProductData from '../common/hooks/useProductData';
import useRetrieveOrder from '../common/hooks/useRetrieveOrder';
import { menus } from '../constants/orderHistoryMenu.constant';
import OrderHistoryNoOrders from './components/OrderHistoryNoOrders.jsx';
import OrderHistoryInvoice from './components/orderHistoryList/OrderHistoryInvoice.jsx';
import OrderHistoryType from './components/orderHistoryList/OrderHistoryType.jsx';
import OrderHistoryPagination from './components/OrderHistoryPagination.jsx';
import { invoiceFilters as invoiceFiltersHelper } from './helpers/invoiceFilters';
import selectBackground from '../images/select-bg.png';
import postMessage from '../utils/postMessage';
import ErrorScreen from '../common/errorScreen/index.js';

const useStyles = makeStyles((theme) => ({
  f1AccountSite: {
    backgroundColor: theme.palette.background.default,
  },
  divider: {
    margin: '20px 0px 5px',
  },
  orderHistoryPanel: {
    marginBottom: '20px',
  },
  orderHistoryTitle: {
    textTransform: 'uppercase',
    [theme.breakpoints.up('lg')]: {
      fontSize: '32px',
    },
    fontWeight: '500',
  },
  orderHistoryPanelBody: {
    padding: '15px 0',
  },
  orderHistoryFilters: {
    marginBottom: '20px',
    paddingRight: '30px',
  },
  orderHistoryDropdown: {
    marginBottom: '30px',
    /*width: 'calc(100% / 3)',
    [theme.breakpoints.down('')]: {
      width: 'calc(100% / 2)',
    },*/
    '&.MuiInputBase-root': {
      border: `1px solid ${theme.palette.custom.lightBlack}`,
      '&.Mui-focused': {
        boxShadow: '0 0 10px #9ecaed',
      },
    },
    '& .MuiNativeSelect-select': {
      height: '50%',
      background: `url(${selectBackground}) no-repeat right center;`,
    },
  },
  menuItem: {
    minHeight: 'unset',
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  hidden: {
    display: 'none',
  },
}));

function OrderHistory(props) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [searchOrdersResponse, searchOrdersFault, requestSearchOrders] =
    useSearchOrders();
  const [productMetaDataResponse, productDataFault, requestProductData] =
    useProductData();
  const [retrieveOrderResponse, retrieveOrderFault, requestRetrieveOrder] =
    useRetrieveOrder();

  const [orderHistoryDateRange, setOrderHistoryDateRange] = useState(1);
  const [showNoOrders, setShowNoOrders] = useState(false);
  const [searchOrders, setSearchOrders] = useState({});
  const [showInvoice, setShowInvoice] = useState(true);
  const [hasProductMetaDataResponse, setHasProductMetaDataResponse] =
    useState(false);
  const [retrieveOrder, setRetrieveOrder] = useState();
  const [pageCount, setPageCount] = useState();
  const [selectedPage, setSelectedPage] = useState(1);
  const [errors, setErrors] = useState([]);
  const [isReady, setIsReady] = useState(false);

  const requestFaults = [
    searchOrdersFault,
    productDataFault,
    retrieveOrderFault,
  ];

  useEffect(() => {
    postMessage('module.showLoader');
    requestSearchOrders(orderHistoryDateRange, selectedPage);
    setShowNoOrders(false);
  }, [orderHistoryDateRange, selectedPage]);

  useEffect(() => {
    const filterFaults = requestFaults.filter((fault) => !!fault);
    if (filterFaults.length) {
      const currentFault = filterFaults[0];
      setErrors([...errors, currentFault]);
      postMessage('module.hideLoader');
      setIsReady(true);
    }
  }, [...requestFaults]);

  useEffect(() => {
    if (searchOrdersResponse) {
      const { Orders } = searchOrdersResponse;

      //no orders
      if (!Orders?.length) {
        setShowNoOrders((prevState) => !prevState);
        postMessage('module.hideLoader');
      } else {
        setSearchOrders(searchOrdersResponse.Orders[0]);

        // Get product ID for Metadata call
        const productId =
          searchOrdersResponse.Orders[0]?.OrderItems[0]?.Product?.Id || null;
        productId &&
          requestProductData({
            id: productId,
          });
      }
      setIsReady(true);
    }
  }, [searchOrdersResponse]);

  useEffect(() => {
    if (searchOrdersResponse && productMetaDataResponse.metadata) {
      const { Orders, PageCount } = searchOrdersResponse;
      setHasProductMetaDataResponse(true);

      const showInvoice = invoiceFiltersHelper(
        Orders,
        productMetaDataResponse.metadata,
      );

      if (showInvoice) {
        requestRetrieveOrder({ OrderId: Orders[0].Id });
        setShowInvoice(true);
      } else {
        setShowInvoice(false);
      }

      PageCount && setPageCount(PageCount);
      postMessage('module.hideLoader');
    }
  }, [productMetaDataResponse]);

  useEffect(() => {
    if (retrieveOrderResponse) {
      postMessage('module.hideLoader', {});
      setRetrieveOrder(retrieveOrderResponse.Order);
    }
  }, [retrieveOrderResponse]);

  const onChangeDropdownOption = (event) => {
    const { value } = event.target;
    setOrderHistoryDateRange(value);
    setSearchOrders(null);
    setRetrieveOrder(null);
  };

  const handleChangePage = (page) => {
    setSelectedPage(Number(page));
    setHasProductMetaDataResponse(false);
    setShowInvoice(false);
    setSearchOrders(null);
    setRetrieveOrder(null);
  };

  useEffect(() => {
    postMessage('module.showLoader');
  }, []);

  useEffect(() => {
    if (isReady) {
      postMessage('module.hideLoader');
    }
  }, [isReady]);

  return (
    <Container
      className={`orderHistoryContainer ${classes.f1AccountSite} ${
        isReady ? '' : classes.hidden
      }`}
    >
      {errors && errors.length ? (
        <Box mt={40}>
          <ErrorScreen
            error={errors[0]}
            mt={5}
            mb={5}
            variant="body1"
            hideBackdrop={true}
          />
        </Box>
      ) : (
        <Box className={classes.orderHistoryPanel}>
          <Typography variant="h2" className={classes.orderHistoryTitle}>
            {t(LocaleKeys.orderHistory.orderHistoryHeader)}
          </Typography>
          <Divider className={classes.divider} />
          <Box
            className={`${classes.orderHistoryPanel} ${classes.orderHistoryPanelBody}`}
          >
            <Grid container>
              <Grid item className={classes.orderHistoryFilters} md={6} lg={4}>
                <FormControl>
                  <NativeSelect
                    data-testid="orderHistory-select"
                    IconComponent={() => null}
                    className={classes.orderHistoryDropdown}
                    onChange={onChangeDropdownOption}
                    value={orderHistoryDateRange}
                    disableUnderline
                  >
                    {menus.map((menu) => (
                      <option
                        key={menu.Value}
                        value={menu.Value}
                        className={classes.menuItem}
                      >
                        {t(menu.name)}
                      </option>
                    ))}
                  </NativeSelect>
                </FormControl>
              </Grid>
            </Grid>
            <Box>
              {showNoOrders && <OrderHistoryNoOrders />}
              {searchOrders &&
                hasProductMetaDataResponse &&
                showInvoice &&
                retrieveOrder && (
                  <OrderHistoryInvoice
                    retrieveOrder={retrieveOrder}
                    showInvoice={showInvoice}
                    productMetadata={productMetaDataResponse?.metadata?.Product}
                  />
                )}
              {searchOrders && hasProductMetaDataResponse && !showInvoice && (
                <OrderHistoryType
                  searchOrders={searchOrders}
                  productMetadata={productMetaDataResponse?.metadata?.Product}
                />
              )}
            </Box>
            {pageCount && pageCount > 1 && (
              <OrderHistoryPagination
                pageCount={pageCount}
                handleChangePage={handleChangePage}
              />
            )}
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default OrderHistory;
